import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ShipmentStatusCategories } from "constants/shipment-statuses.config";
import { ApiLoadingStatus } from "enum/api-loading-status.enum";
import { statuses } from "enum/shipment-statuses.enum";
import { ShipmentStatusCategoryStrings } from "enum/shipment-status-category-string.enum";
import { IShipment } from "models/shipment/shipment.model";
import { FetchShipmentsResponseDto } from "services/api/shipments/dtos/fetch-shipments.response.dto";
import { IShipmentsSlice } from "./ShipmentsSlice.interface";
import {
  isDataUpdated,
  ShipmentsSliceActions,
} from "./ShipmentsSliceActions.enum";
import { ShipmentsInitialState } from "./ShipmentsSliceInitialState.const";
import { isModalClosed } from "store/modalSlice/modalSlice.actions";
import { modals } from "store/modalSlice/modalSlice.types";

export const SHIPMENTS_SLICE_NAME = "shipmentsSlice";

export const shipmentsSlice = createSlice({
  name: SHIPMENTS_SLICE_NAME,
  initialState: ShipmentsInitialState,
  reducers: {
    [ShipmentsSliceActions.SET_DATA]: (
      state,
      action: PayloadAction<FetchShipmentsResponseDto>
    ) => {
      state.items = action.payload.data;

      // sort items by index

      state.meta = action.payload.meta;
    },
    setSelectedShipmentIds: (
      state,
      action: PayloadAction<IShipmentsSlice["selectedShipmentIds"]>
    ) => {
      state.selectedShipmentIds = action.payload;
    },
    setCurrentShipmentLoadingStatus: (
      state,
      action: PayloadAction<ApiLoadingStatus>
    ) => {
      state.currentShipmentLoadingStatus = action.payload;
    },
    setQuerySearch: (
      state,
      action: PayloadAction<IShipmentsSlice["query"]["filters"]["search"]>
    ) => {
      state.query.filters.search = action.payload;
      state.query.offset = 0;
    },
    setQueryBulkShipment: (
      state,
      action: PayloadAction<IShipmentsSlice["query"]["filters"]["bulkShipment"]>
    ) => {
      state.query.filters.bulkShipment = action.payload;
      state.query.offset = 0;
    },
    setQueryOffset: (
      state,
      action: PayloadAction<IShipmentsSlice["query"]["offset"]>
    ) => {
      state.query.offset = action.payload;
    },
    setQueryLabelJob: (
      state,
      action: PayloadAction<IShipmentsSlice["query"]["filters"]["labelJob"]>
    ) => {
      state.query.filters.labelJob = action.payload;
      state.query.offset = 0;
    },
    setQueryFromDate: (
      state,
      action: PayloadAction<IShipmentsSlice["query"]["filters"]["fromDate"]>
    ) => {
      state.query.filters.fromDate = action.payload;
      state.query.offset = 0;
    },
    setQueryToDate: (
      state,
      action: PayloadAction<IShipmentsSlice["query"]["filters"]["toDate"]>
    ) => {
      state.query.filters.toDate = action.payload;
      state.query.offset = 0;
    },
    setQueryDateRangeField: (
      state,
      action: PayloadAction<
        IShipmentsSlice["query"]["filters"]["dateRangeField"]
      >
    ) => {
      state.query.filters.dateRangeField = action.payload;
      state.query.offset = 0;
    },
    setQuerySort: (
      state,
      action: PayloadAction<IShipmentsSlice["query"]["sort"]>
    ) => {
      state.query.sort = action.payload;
      state.query.offset = 0;
    },
    setQueryFromAddressCountryIso: (
      state,
      action: PayloadAction<
        IShipmentsSlice["query"]["filters"]["fromAddressCountryIso"]
      >
    ) => {
      state.query.filters.fromAddressCountryIso = action.payload;
      state.query.offset = 0;
    },
    setQueryToAddressCountryIso: (
      state,
      action: PayloadAction<
        IShipmentsSlice["query"]["filters"]["toAddressCountryIso"]
      >
    ) => {
      state.query.filters.toAddressCountryIso = action.payload;
      state.query.offset = 0;
    },
    setQueryStatusFilter: (
      state,
      action: PayloadAction<ShipmentStatusCategoryStrings>
    ) => {
      state.query.filters.statuses = ShipmentStatusCategories[action.payload];

      state.items = [];

      state.selectedShipmentIds = [];

      // state.query.filters.search = "";

      // state.query.sort = {
      //   createdDate: SortDirection.DESC,
      // };

      state.query.filters.trackingStatus = null;

      // The Status pill is Complete-tab only, so its selections never carry
      // across a tab switch.
      state.query.filters.shipmentStatuses = null;

      state.query.filters.storeUpdateStatuses = null;

      state.meta.shipmentIds = [];

      state.query.offset = 0;

      state.loadingStatus = ApiLoadingStatus.LOADING;
    },
    setQueryToDefault: (state) => {
      // Preserve the current status tab: every caller navigates to a
      // ?status= URL right after this reset, and letting the reset flip
      // statuses back to the READY default made the fetch effect fire once
      // with the wrong tab's statuses before that navigation landed.
      state.query = {
        ...ShipmentsInitialState.query,
        filters: {
          ...ShipmentsInitialState.query.filters,
          statuses: state.query.filters.statuses,
        },
      };
    },
    setSupportDraftResume: (
      state,
      action: PayloadAction<IShipmentsSlice["supportDraftResume"]>
    ) => {
      state.supportDraftResume = action.payload;
    },
    setCurrentShipment: (state, action: PayloadAction<IShipment>) => {
      state.currentShipment = action.payload;

      // sort packages by index

      state.currentShipment.packages =
        state.currentShipment?.packages?.sort((a, b) => a.index - b.index) ||
        [];

      state.currentShipmentLoadingStatus = "SUCCESS";
    },
    // Shows a label's Label Download Date straight away, without reloading the
    // list. Mirrors the server: first time only, and only if it has a label.
    setLabelsDownloaded: (
      state,
      action: PayloadAction<{
        shipmentIds: IShipment["id"][];
        downloadedAt: string;
      }>
    ) => {
      const { shipmentIds, downloadedAt } = action.payload;
      const ids = new Set(shipmentIds.map(String));
      const stamp = (shipment: IShipment | null | undefined) => {
        if (
          shipment &&
          ids.has(String(shipment.id)) &&
          !shipment.labelDownloadedAt &&
          (shipment.status === statuses.label_created ||
            shipment.status === statuses.cancelled)
        ) {
          shipment.labelDownloadedAt = downloadedAt;
        }
      };
      state.items.forEach(stamp);
      stamp(state.currentShipment);
    },
    setShowMore: (state, action: PayloadAction<boolean>) => {
      state.showMore = action.payload;
    },
    // Optimistic flip after "Retry now": the server re-queues the update and
    // the row should read as sending immediately, before the refetch lands.
    setCurrentShipmentIntegrationUpdatePending: (state) => {
      if (state.currentShipment?.integrationUpdate) {
        state.currentShipment.integrationUpdate = {
          ...state.currentShipment.integrationUpdate,
          status: "pending",
          errorCode: null,
          lastDispatchedAt: new Date().toISOString(),
        };
      }
    },
    // Silent poll refresh: replaces only the sync-row field so the rest of
    // the open drawer never re-renders from a background fetch. Guarded by
    // uuid so a late response can't land on a different shipment.
    setCurrentShipmentIntegrationUpdate: (
      state,
      action: PayloadAction<{
        uuid: IShipment["uuid"];
        integrationUpdate: IShipment["integrationUpdate"];
      }>
    ) => {
      if (state.currentShipment?.uuid === action.payload.uuid) {
        state.currentShipment.integrationUpdate =
          action.payload.integrationUpdate;
      }
    },
    setStatus: (state, action: PayloadAction<ApiLoadingStatus>) => {
      state.loadingStatus = action.payload;
    },
    setLoadingRerunRules: (state, action: PayloadAction<ApiLoadingStatus>) => {
      state.loadingRerunRules = action.payload;
    },
    setReportShipmentFrom: (state, action: PayloadAction<string>) => {
      state.reportShipmentFrom = action.payload;
    },
    setReportShipmentTo: (state, action: PayloadAction<string>) => {
      state.reportShipmentTo = action.payload;
    },
    setReportLabelFrom: (state, action: PayloadAction<string>) => {
      state.reportLabelFrom = action.payload;
    },
    setReportLabelTo: (state, action: PayloadAction<string>) => {
      state.reportLabelTo = action.payload;
    },
    setManifestShipmentFrom: (state, action: PayloadAction<string>) => {
      state.manifestShipmentFrom = action.payload;
    },
    setManifestShipmentTo: (state, action: PayloadAction<string>) => {
      state.manifestShipmentTo = action.payload;
    },
    setTrackingStatuses: (state, action: PayloadAction<string[]>) => {
      state.listOfTrackingStatuses = action.payload ?? [];
    },
    setAllIntegrationStores: (
      state,
      action: PayloadAction<IShipmentsSlice["allIntegrationStores"]>
    ) => {
      state.allIntegrationStores = action.payload ?? [];
    },
    setAllIntegrationStoresLoadingStatus: (
      state,
      action: PayloadAction<IShipmentsSlice["allIntegrationStoresLoadingStatus"]>
    ) => {
      state.allIntegrationStoresLoadingStatus = action.payload;
    },
    setAllCarriers: (
      state,
      action: PayloadAction<IShipmentsSlice["allCarriers"]>
    ) => {
      state.allCarriers = action.payload;
    },
    setAllCarriersLoadingStatus: (
      state,
      action: PayloadAction<IShipmentsSlice["allCarriersLoadingStatus"]>
    ) => {
      state.allCarriersLoadingStatus = action.payload;
    },
    setQueryTrackingStatus: (
      state,
      action: PayloadAction<
        IShipmentsSlice["query"]["filters"]["trackingStatus"]
      >
    ) => {
      state.query.filters.trackingStatus = action.payload;
    },
    setQueryIntegrationStoreId: (
      state,
      action: PayloadAction<
        IShipmentsSlice["query"]["filters"]["integrationStoreId"]
      >
    ) => {
      state.query.filters.integrationStoreId = action.payload;
    },
    setQueryStoreName: (
      state,
      action: PayloadAction<IShipmentsSlice["query"]["filters"]["storeName"]>
    ) => {
      state.query.filters.storeName = action.payload;
      state.query.offset = 0;
    },
    setQuerySku: (
      state,
      action: PayloadAction<IShipmentsSlice["query"]["filters"]["sku"]>
    ) => {
      state.query.filters.sku = action.payload;
    },
    setQueryDescription: (
      state,
      action: PayloadAction<IShipmentsSlice["query"]["filters"]["description"]>
    ) => {
      state.query.filters.description = action.payload;
    },
    setQueryQuantity: (
      state,
      action: PayloadAction<IShipmentsSlice["query"]["filters"]["quantity"]>
    ) => {
      state.query.filters.quantity = action.payload;
    },
    setQueryItemQuantityGreaterThan: (
      state,
      action: PayloadAction<
        IShipmentsSlice["query"]["filters"]["itemQuantityGreaterThan"]
      >
    ) => {
      state.query.filters.itemQuantityGreaterThan = action.payload;
      state.query.offset = 0;
    },
    setQueryItemQuantityLessThan: (
      state,
      action: PayloadAction<
        IShipmentsSlice["query"]["filters"]["itemQuantityLessThan"]
      >
    ) => {
      state.query.filters.itemQuantityLessThan = action.payload;
      state.query.offset = 0;
    },
    setQueryShipmentSourceName: (
      state,
      action: PayloadAction<
        IShipmentsSlice["query"]["filters"]["shipmentSourceName"]
      >
    ) => {
      state.query.filters.shipmentSourceName = action.payload;
    },
    setQueryCarrierName: (
      state,
      action: PayloadAction<IShipmentsSlice["query"]["filters"]["carrierName"]>
    ) => {
      state.query.filters.carrierName = action.payload;
      state.query.offset = 0;
    },
    setQueryCarrierServices: (
      state,
      action: PayloadAction<
        IShipmentsSlice["query"]["filters"]["carrierServices"]
      >
    ) => {
      state.query.filters.carrierServices = action.payload;
      state.query.offset = 0;
    },
    setQueryShipmentStatuses: (
      state,
      action: PayloadAction<
        IShipmentsSlice["query"]["filters"]["shipmentStatuses"]
      >
    ) => {
      state.query.filters.shipmentStatuses = action.payload;
      state.query.offset = 0;
    },
    setQueryStoreUpdateStatuses: (
      state,
      action: PayloadAction<
        IShipmentsSlice["query"]["filters"]["storeUpdateStatuses"]
      >
    ) => {
      state.query.filters.storeUpdateStatuses = action.payload;
      state.query.offset = 0;
    },
    setDateRangeDays: (
      state,
      action: PayloadAction<IShipmentsSlice["dateRangeDays"]>
    ) => {
      state.dateRangeDays = action.payload;
    },
    setBulkShipmentLookupValue: (
      state,
      action: PayloadAction<IShipmentsSlice["bulkShipmentLookupValue"]>
    ) => {
      state.bulkShipmentLookupValue = action.payload;
    },
    setSurchargeReportFrom: (state, action: PayloadAction<string>) => {
      state.surchargeReportFrom = action.payload;
    },
    setSurchargeReportTo: (state, action: PayloadAction<string>) => {
      state.surchargeReportTo = action.payload;
    },
  },
  extraReducers: (builder) => {
    // If user has selected one or more shipments, and then edits a shipment which results in a refetch of the data, the deleted shipment should be removed from the selection.
    builder.addMatcher(isDataUpdated, (state) => {
      state.selectedShipmentIds = state.selectedShipmentIds.filter((id) => {
        return state.meta?.shipmentIds?.includes(id);
      });
    });
    builder.addMatcher(
      isModalClosed,
      (state, action: { payload: { modalName: modals } }) => {
        if (action.payload.modalName === "viewShipment") {
          state.showMore = false;
        }
        if (action.payload.modalName === "shipmentReport") {
          state.reportShipmentFrom = new Date().toISOString();
          state.reportShipmentTo = new Date().toISOString();
          state.manifestShipmentFrom = new Date().toISOString();
          state.manifestShipmentTo = new Date().toISOString();
        }
        if (action.payload.modalName === "labelReport") {
          state.reportLabelFrom = new Date().toISOString();
          state.reportLabelTo = new Date().toISOString();
        }
      }
    );
  },
});
export const {
  setLabelsDownloaded,
  setSupportDraftResume,
  setData,
  setStatus,
  setLoadingRerunRules,
  setQueryFromAddressCountryIso,
  setQueryToAddressCountryIso,
  setQueryTrackingStatus,
  setQueryFromDate,
  setQueryToDate,
  setQueryDateRangeField,
  setQueryLabelJob,
  setQuerySort,
  setQueryStatusFilter,
  setQueryBulkShipment,
  setQueryOffset,
  setQuerySearch,
  setCurrentShipmentLoadingStatus,
  setSelectedShipmentIds,
  setCurrentShipment,
  setShowMore,
  setCurrentShipmentIntegrationUpdatePending,
  setCurrentShipmentIntegrationUpdate,
  setQueryToDefault,
  setReportShipmentFrom,
  setReportShipmentTo,
  setReportLabelFrom,
  setReportLabelTo,
  setManifestShipmentFrom,
  setManifestShipmentTo,
  setBulkShipmentLookupValue,
  setTrackingStatuses,
  setAllIntegrationStores,
  setAllIntegrationStoresLoadingStatus,
  setAllCarriers,
  setAllCarriersLoadingStatus,
  setQueryIntegrationStoreId,
  setQueryStoreName,
  setQuerySku,
  setQueryDescription,
  setQueryQuantity,
  setQueryItemQuantityGreaterThan,
  setQueryItemQuantityLessThan,
  setQueryShipmentSourceName,
  setQueryCarrierName,
  setQueryCarrierServices,
  setQueryShipmentStatuses,
  setQueryStoreUpdateStatuses,
  setDateRangeDays,
  setSurchargeReportFrom,
  setSurchargeReportTo,
} = shipmentsSlice.actions;
